import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { apiParameters } from 'src/app/configs/api.params';
import { StorageService } from './storage/storage.service';
import { ICompany } from '../models/company.model';
import { themeColors } from '../models/colors';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  public apiEndPoint = apiParameters.url;
  private colors = themeColors;

  constructor(private http: HttpClient, private storage: StorageService) {}

  /**
   * Get all companies from backend
   * @returns - List of companies.
   */
  getCompanies(name?:string): Observable<ICompany[]> {

    let searchParams = new HttpParams();

    if (name && name !== '') {
      searchParams = searchParams.append('name', name);
    }

    const httpOptions = {
      params: searchParams,
    };


    return this.http.get(`${apiParameters.url}/companies`, httpOptions).pipe(
      map((res: ICompany[]) => {
        res.sort((a, b) => {
          return (
            (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any)
          );
        });
        return res;
      })
    );
  }

  /**
   * Get one company from backend
   * @param companyId - is id of the selected company
   * @returns One company object
   */

  getCompanyById(companyId: string): Observable<ICompany> {
    return this.http
      .get<ICompany>(`${apiParameters.url}/companies/${companyId}`)
      .pipe(
        map((res: ICompany) => {
          if (res.whitelabel) {
            res.whitelabel.colors = Object.keys(res.whitelabel).reduce(
              (arr, key) => {
                let color = { name: key, value: res.whitelabel[key] };
                return arr.concat(color);
              },
              []
            );

            res.whitelabel.colors = res.whitelabel.colors.filter((item) =>
              this.colors.includes(item.name)
            );
          }

          return res;
        })
      );
  }

  /**
   * Create new company
   * @param body - is company data - e.g. (name: 'Audi', address: 'Sveti Sava 10', phone: '+12346789')
   * @returns Newly created company
   */
  createCompany(body): Observable<ICompany> {
    return this.http.post<ICompany>(`${apiParameters.url}/companies`, body);
  }

  editCompany(companyId: string, body): Observable<ICompany> {
    return this.http.patch<ICompany>(
      `${apiParameters.url}/companies/${companyId}`,
      body
    );
  }

  deleteCompany(companyId: string): Observable<any> {
    return this.http.delete(`${apiParameters.url}/companies/${companyId}`);
  }

  addCredit(companyId: string, body): Observable<any> {
    return this.http.post(
      `${apiParameters.url}/companies/${companyId}/credit`,
      body
    );
  }

  getCreditsHistory(
    companyId: string,
    page: number,
    limit: number
  ): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString(),
      }),
    };

    return this.http.get(
      `${apiParameters.url}/companies/${companyId}/credits-history`,
      httpOptions
    );
  }

  /**
   * This method is for getting company vin decode logs.
   * @param companyId - id of the company
   * @param page - number of page for pagination
   * @param limit - items for page
   * @param userId - optional query filter
   * @param from - optional query filter
   * @param to - optional query filter
   * @returns
   */
  vinDecodeLog(
    companyId: string,
    page: number,
    limit: number,
    userId: string,
    from?: string,
    to?: string
  ): Observable<any> {
    let searchParams = new HttpParams();

    userId && userId !== ''
      ? (searchParams = searchParams.append('userId', userId))
      : null;
    from && from !== ''
      ? (searchParams = searchParams.append('from', from))
      : null;
    to && to !== '' ? (searchParams = searchParams.append('to', to)) : null;

    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString(),
      }),
      params: searchParams,
    };

    return this.http.get(
      `${apiParameters.url}/companies/${companyId}/vin-decode-log`,
      httpOptions
    );
  }

  /**
   * This method is for getting company vin decode logs as csv.
   * @param companyId - id of the company
   * @param userId - optional query filter
   * @param from - optional query filter
   * @param to - optional query filter
   * @returns
   */
  downloadCSV(
    companyId: string,
    userId: string,
    from?: string,
    to?: string
  ): Observable<any> {
    let searchParams = new HttpParams();

    userId && userId !== ''
      ? (searchParams = searchParams.append('userId', userId))
      : null;
    from && from !== ''
      ? (searchParams = searchParams.append('from', from))
      : null;
    to && to !== '' ? (searchParams = searchParams.append('to', to)) : null;

    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'text/csv' }),
      responseType: 'text' as 'text',
      params: searchParams,
    };

    return this.http.get(
      `${apiParameters.url}/companies/${companyId}/vin-decode-log-csv`,
      httpOptions
    );
  }

  /**
   * This method is for getting company settings.
   * @param companyId - id of the company
   * @returns
   */
  getSettings(companyId: string): Observable<any> {
    return this.http.get(
      `${apiParameters.url}/companies/${companyId}/app-settings`
    );
  }

  editSettings(companyId: string, body): Observable<ICompany> {
    return this.http.put<ICompany>(
      `${apiParameters.url}/companies/${companyId}/app-settings`,
      body
    );
  }
}
